import { Button } from '@app/components/common/buttons/Button/Button';
import { Spinner } from '@app/components/common/Spinner/Spinner';
import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Document, pdfjs } from 'react-pdf';
import 'react-pdf/dist/esm/Page/AnnotationLayer.css';
import 'react-pdf/dist/esm/Page/TextLayer.css';
import * as S from './PriceQuote.styles';
import { IFile } from '@app/interfaces/file';
import { getFileWithSignedUrl } from '@app/api/file.api';
import { openFile } from '@app/utils/file';

pdfjs.GlobalWorkerOptions.workerSrc = 'assets/js/pdf.worker.min.js';

interface DynamicPriceQuoteProps {
  open: boolean;
  file?: string;
  link?: string;
}

export const DynamicPriceQuote: React.FC<DynamicPriceQuoteProps> = ({ open, file, link }) => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(true);
  const [fileLink, setFileLink] = useState<IFile | null>(null);
  const [loadedUrl, setLoadedUrl] = useState<string | null>(null);
  const [numPages, setNumPages] = useState<number>(0);
  const [pageNumber, setPageNumber] = useState(1);
  const [error, setError] = useState<string | null>(null);
  const [width, setWidth] = useState(window.innerWidth);

  const onDocumentLoadSuccess = useCallback(({ numPages }) => {
    setError(null);
    setNumPages(numPages);
    setLoading(false);
  }, []);

  const onDocumentLoadError = useCallback(() => {
    setError(t('dealRooms.sections.failedToLoad'));
    setLoading(false);
  }, [t]);

  const goToPrevPage = useCallback(() => {
    setPageNumber((prev) => Math.max(prev - 1, 1));
  }, []);

  const goToNextPage = useCallback(() => {
    setPageNumber((prev) => Math.min(prev + 1, numPages || 1));
  }, [numPages]);

  const onSignDocument = useCallback(() => {
    if (link) {
      window.open(link, '_blank');
    }
  }, [link]);

  useEffect(() => {
    if (fileLink) {
      setLoadedUrl(fileLink?.signedUrl as string);
    }
  }, [fileLink]);

  useEffect(() => {
    const fetchFile = async (id: string) => {
      try {
        const file = await getFileWithSignedUrl(id);
        setFileLink(file);
      } catch (error) {
        setError((error as Error).message);
      }
    };

    if (file) {
      // Destroy all canvases created by PDF.js
      const canvases = document.querySelectorAll('canvas');
      canvases.forEach((canvas) => {
        const ctx = canvas.getContext('2d');
        if (ctx) ctx.clearRect(0, 0, canvas.width, canvas.height);
      });

      // Optionally remove from DOM (optional)
      const layers = document.querySelectorAll('.react-pdf__Page__canvas');
      layers.forEach((layer) => layer.remove());
      fetchFile(file as string);
    } else {
      setLoading(false);
    }
  }, [file]);

  useEffect(() => {
    const handleResize = () => {
      const container = document.getElementById('price-quote');
      if (container) {
        setWidth(container.offsetWidth - 60);
      }
    };
    window.addEventListener('resize', handleResize);
    handleResize();
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  return (
    <>
      {open && (
        <div id="price-quote">
          <Spinner spinning={loading}>
            {file && (
              <S.FooterWrapper className="top">
                <S.ButtonContainer>
                  <Button type="primary" onClick={goToPrevPage} disabled={pageNumber <= 1}>
                    {t('dealRooms.sections.previous')}
                  </Button>
                  <S.ButtonText>
                    {t('dealRooms.sections.pageOf', {
                      page: pageNumber,
                      totalPages: numPages,
                    })}
                  </S.ButtonText>
                  <Button type="primary" onClick={goToNextPage} disabled={numPages ? pageNumber >= numPages : true}>
                    {t('dealRooms.sections.next')}
                  </Button>
                </S.ButtonContainer>
                <S.DocumentLink
                  onClick={(e) => {
                    e.stopPropagation();
                    openFile(file);
                  }}
                >
                  {t('dealRooms.sections.downloadDocumentAsPdf', 'הורד מסמך כ - PDF')}
                </S.DocumentLink>
              </S.FooterWrapper>
            )}
            {file && (
              <>
                <Document
                  file={loadedUrl}
                  onLoadSuccess={onDocumentLoadSuccess}
                  onLoadError={onDocumentLoadError}
                  loading={<S.LoadingMessage>{t('dealRooms.sections.loadingPDf')}</S.LoadingMessage>}
                  error={null}
                >
                  {!error && (
                    <S.PageContainer
                      key={`${pageNumber}-${Date.now()}`}
                      pageNumber={pageNumber}
                      renderTextLayer={false}
                      renderAnnotationLayer={false}
                      className="shadow-lg"
                      width={width}
                    />
                  )}
                </Document>
              </>
            )}
            {file && (
              <S.FooterWrapper>
                <>
                  {error ? (
                    <S.ErrorMessage>{error}</S.ErrorMessage>
                  ) : (
                    <S.ButtonContainer>
                      <Button type="primary" onClick={goToPrevPage} disabled={pageNumber <= 1}>
                        {t('dealRooms.sections.previous')}
                      </Button>
                      <S.ButtonText>
                        {t('dealRooms.sections.pageOf', {
                          page: pageNumber,
                          totalPages: numPages,
                        })}
                      </S.ButtonText>
                      <Button type="primary" onClick={goToNextPage} disabled={numPages ? pageNumber >= numPages : true}>
                        {t('dealRooms.sections.next')}
                      </Button>
                    </S.ButtonContainer>
                  )}

                  <S.DocumentLink
                    onClick={(e) => {
                      e.stopPropagation();
                      openFile(file);
                    }}
                  >
                    {t('dealRooms.sections.downloadDocumentAsPdf', 'הורד מסמך כ - PDF')}
                  </S.DocumentLink>
                </>
              </S.FooterWrapper>
            )}
            {link && (
              <S.FooterWrapper className={`sign-button ${!file ? 'only-sign' : ''}`}>
                <S.SignButton type="primary" onClick={onSignDocument}>
                  {t('dealRooms.sections.signDocument')}
                </S.SignButton>
              </S.FooterWrapper>
            )}
          </Spinner>
        </div>
      )}
    </>
  );
};
